import { CATEGORY_FACETS } from '@constants/categories';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { updateAllFieldsChange } from '@tkmp/rfq/redux/actionCreators';
import type { InlineFields } from '@tkmp/rfq/types';
import type { CategoryCode } from '@typings/Category';
import { useAtomValue } from 'jotai';
import { useCallback, useMemo, useState } from 'react';
import {
	networkAtom,
	vendorNetworkCountAtom,
	vendorNetworkPvRecCountAtom,
} from '../../../jotai/storefront/storefronts-network-pvrec';
import codeToSlugNameMap from '../../../utils/categoryNameMappings';
import { createRfqFieldsFromMember } from '../../../utils/rfq';
import { getUniqueRecommendedVendors } from '../utils/vendorMapping';

export const useVendorNetworkData = (isMobile: boolean) => {
	const dispatch = useAppDispatch();

	const [selectedCategoryCode, setSelectedCategoryCode] =
		useState<CategoryCode | null>(null);
	const [showVrmConfirmation, setShowVrmConfirmation] = useState(false);
	const [contactedVendors, setContactedVendors] = useState<
		API.FESharedProfile[]
	>([]);
	const [justContactedIds, setJustContactedIds] = useState<Set<string>>(
		new Set(),
	);

	const vendorRaw = useAppSelector((state) => state.vendor.vendorRaw);
	const member = useAppSelector((state) => state.membership.member);
	const conversations = useAppSelector(
		(state) => state.messagedVendors.conversations,
	);

	const vendorNetwork = useAtomValue(networkAtom);
	const vendorNetworkRecCount = useAtomValue(vendorNetworkCountAtom);
	const recommendationCount = useAtomValue(vendorNetworkPvRecCountAtom);

	const allContactedIds = useMemo(() => {
		const ids = new Set(Object.keys(conversations));
		for (const id of justContactedIds) {
			ids.add(String(id));
		}
		return ids;
	}, [conversations, justContactedIds]);

	const selectedCategory = useMemo(
		() =>
			vendorNetwork.find((cat) => cat.categoryCode === selectedCategoryCode),
		[vendorNetwork, selectedCategoryCode],
	);

	const categoryName = useMemo(
		() =>
			(selectedCategoryCode &&
				(CATEGORY_FACETS[selectedCategoryCode]?.name ||
					codeToSlugNameMap[selectedCategoryCode]?.name)) ??
			'vendors',
		[selectedCategoryCode],
	);

	const vrmTitle = useMemo(() => {
		const hasVendorNetwork =
			selectedCategory && selectedCategory?.storefrontNetwork.length > 0;

		return hasVendorNetwork
			? `Reach out to ${categoryName} who know your venue`
			: `Reach out to ${categoryName} to plan your wedding`;
	}, [categoryName, selectedCategory]);

	const vrmTextArea =
		'Hi, we are looking to complete our wedding team, we would like more information on your packages and pricing. Thank you!';

	const recommendedVendors = useMemo(() => {
		const vendors = getUniqueRecommendedVendors(
			selectedCategory,
			allContactedIds,
			isMobile,
		);

		return vendors.filter((v) => !allContactedIds.has(String(v.id)));
	}, [selectedCategory, allContactedIds, isMobile]);

	const handleCategoryClick = useCallback(
		(categoryCode: CategoryCode) => {
			if (member) {
				dispatch(
					updateAllFieldsChange({
						fields: createRfqFieldsFromMember(member) as InlineFields,
					}),
				);
			}
			setSelectedCategoryCode(categoryCode);
		},
		[member, dispatch],
	);

	const handleVrmClose = useCallback(() => {
		setSelectedCategoryCode(null);
		return { type: 'CLOSE_VRM_MODAL' };
	}, []);

	const handleVrmSubmit = useCallback(
		async (successes: API.SendBulkInquirySuccess[]) => {
			const newIds = successes.map((s) => String(s.storefrontId));

			const populatedVendors = successes.map((success) => {
				const vendor = recommendedVendors.find(
					(v) => String(v.id) === String(success.storefrontId),
				);
				return { ...success, ...vendor } as API.FESharedProfile;
			});

			setJustContactedIds((prev) => {
				const updated = new Set(prev);
				for (const id of newIds) updated.add(id);
				return updated;
			});

			setContactedVendors(populatedVendors);
			setShowVrmConfirmation(true);
		},
		[recommendedVendors],
	);

	const handleVrmConfirmationClose = useCallback(() => {
		setShowVrmConfirmation(false);
		setSelectedCategoryCode(null);
		setContactedVendors([]);
	}, []);

	return {
		member,
		vendorRaw,
		selectedCategoryCode,
		showVrmConfirmation,
		contactedVendors,
		allContactedIds,
		recommendedVendors,
		categoryName,
		vrmTitle,
		vrmTextArea,
		selectedCategory,
		vendorNetworkRecCount,
		recommendationCount,
		handleCategoryClick,
		handleVrmClose,
		handleVrmSubmit,
		handleVrmConfirmationClose,
	};
};
